import { IconButton, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { InfoModal } from '@/commons/info-modal';
import InfoIcon from '@mui/icons-material/Info';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { CreateAdFormData } from '@/pages/ad-pages/CreateAd';
import { Card } from '@/features/card';

type Props = {
  control: Control<CreateAdFormData>;
  errors: FieldErrors<CreateAdFormData>;
  valueFrom?: string;
  valueTo?: string;
  setValueFrom: (value: string | null) => void;
  setValueTo: (value: string | null) => void;
};

export const DisplayHoursSection: FC<Props> = ({
  control,
  setValueFrom,
  setValueTo,
}) => {
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const { t } = useTranslation();

  const handleFromChange = (newValue: Dayjs | null) => {
    if (newValue) {
      setValueFrom(newValue.format('HH:mm:ss'));
    } else {
      setValueFrom(null);
    }
  };

  const handleToChange = (newValue: Dayjs | null) => {
    if (newValue) {
      setValueTo(newValue.format('HH:mm:ss'));
    } else {
      setValueTo(null);
    }
  };

  return (
    <div className="w-full flex flex-col">
      <Typography variant="h6" sx={{ mb: '8px' }}>
        <Trans i18nKey="display_hours_section.title" />
        <IconButton size="medium" onClick={() => setInfoModalOpen(true)}>
          <InfoIcon fontSize="inherit" />
        </IconButton>
      </Typography>

      <Card className="flex flex-col w-full md:flex-row">
        <div className="flex items-center justify-center w-full">
          <div className="flex flex-col md:flex-row gap-3 items-center justify-center flex-1/2">
            <Controller
              name="startHour"
              control={control}
              render={({ field: { onChange, value } }) => {
                const handleChange = (value: Dayjs | null) => {
                  onChange(value ? value.format('HH:mm:ss') : '');
                  handleFromChange(value);
                };

                return (
                  <TimePicker
                    label={t('date_time_picker.from')}
                    value={value ? dayjs(`2000-01-01 ${value}`) : null}
                    onChange={handleChange}
                    sx={{ flexGrow: 1, width: '100%' }}
                  />
                );
              }}
            />

            <div className="mx-4 hidden md:block">
              <ArrowForwardIcon />
            </div>

            <Controller
              name="endHour"
              control={control}
              render={({ field: { onChange, value } }) => {
                const handleChange = (value: Dayjs | null) => {
                  onChange(value ? value.format('HH:mm:ss') : '');
                  handleToChange(value);
                };

                return (
                  <TimePicker
                    label={t('date_time_picker.to')}
                    value={value ? dayjs(`2000-01-01 ${value}`) : null}
                    onChange={handleChange}
                    sx={{ flexGrow: 1, width: '100%' }}
                  />
                );
              }}
            />
          </div>
        </div>
        <InfoModal
          title={t('display_hours_section.title')}
          open={infoModalOpen}
          onClose={() => setInfoModalOpen(false)}
        >
          <Trans i18nKey="display_hours_section.main_info_content" />
        </InfoModal>
      </Card>
    </div>
  );
};
